import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X } from "lucide-react";

/**
 * Feb 2026 — In-app prompt untuk mengaktifkan Web Push.
 * Muncul sekali per perangkat (localStorage flag) untuk user yang login
 * dan browser-nya mendukung push tetapi belum meminta izin.
 *
 * Design goals:
 *   • Non-intrusive: banner tipis di atas main content, ada tombol tutup.
 *   • Dismissal persisten: user yang tutup sekali tidak akan diprompt lagi
 *     di perangkat itu (kecuali localStorage dibersihkan).
 *   • Tidak muncul bila izin sudah "granted" atau "denied" (Chrome sudah
 *     memutuskan) — hanya kondisi "default" yang ditarget.
 */
const DISMISS_KEY = "aroma_push_prompt_dismissed_v1";

export default function PushPromptBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Feature check — SW + Push + Notification API.
    const supported =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    if (!supported) return;
    // Skip bila user sudah memilih (granted/denied) atau sudah dismiss.
    if (Notification.permission !== "default") return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    setVisible(true);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch { /* quota / private mode — abaikan */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="border-b border-[#BFDBFE] bg-[#EFF6FF] px-4 py-2.5"
      data-testid="push-prompt-banner"
      role="region"
      aria-label="Aktifkan notifikasi"
    >
      <div className="max-w-5xl mx-auto flex items-center gap-3">
        <span className="hidden sm:inline-flex h-8 w-8 rounded-md bg-[#0055FF]/10 text-[#0055FF] items-center justify-center shrink-0">
          <Bell className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-[#1E3A8A] leading-tight">
            Aktifkan notifikasi di perangkat ini
          </div>
          <div className="text-xs text-[#1E40AF] mt-0.5">
            Terima update tiket, absensi, dan pengajuan langsung — bahkan saat aplikasi tertutup.
          </div>
        </div>
        <Link
          to="/settings"
          onClick={dismiss}
          className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs font-bold transition-colors"
          data-testid="push-prompt-cta"
        >
          <Bell className="h-3.5 w-3.5" />
          Aktifkan
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-md text-[#1E40AF] hover:bg-[#DBEAFE]"
          aria-label="Tutup ajakan aktifkan notifikasi"
          data-testid="push-prompt-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
